import './attachment-viewer.css';

/**
 * Reusable in-app attachment preview viewer (Alpine component: 'attachmentViewer').
 *
 * Pairs with the template fragment core/component/attachment-viewer/attachment-viewer :: viewer.
 * Listens for the 'open-attachment-preview' window event and shows a native Bootstrap modal
 * rendering the document inline: PDFs in an <iframe>, images in an <img>. The document is served
 * either by a backend preview endpoint (Content-Disposition: inline) or, for a not-yet-uploaded
 * file, by an object URL built client-side. Nothing is downloaded.
 *
 * Visibility is driven by us (not the Bootstrap Modal JS): `visible` mounts the modal via the
 * .avm-open class, and `shown` flips the opacity one frame later so the CSS fade transition plays.
 *
 * Event detail: { url: string, filename: string }
 */
export default () => ({
    visible: false, // mounts the modal (display, via .avm-open)
    shown: false,   // drives the opacity transition (fade), flipped one tick after `visible`
    url: '',
    filename: '',
    _closeTimer: null,

    get isPdf() {
        return /\.pdf\b/i.test(this.filename) || /\.pdf\b/i.test(this.url);
    },

    get frameSrc() {
        if (!this.url) return '';
        // The PDF open-parameter #navpanes=0 collapses the sidebar, but appending a fragment
        // breaks blob:/data: URLs in the built-in viewer, so only add it to server-served URLs.
        return (this.url.startsWith('blob:') || this.url.startsWith('data:')) ? this.url : `${this.url}#navpanes=0`;
    },

    openPreview(detail) {
        if (!detail || !detail.url) return;
        clearTimeout(this._closeTimer);
        this._revokeObjectUrl();
        this.url = detail.url;
        this.filename = detail.filename || '';
        this.visible = true;
        // Flip opacity on the next frame so the CSS transition plays (fade-in)
        this.$nextTick(() => { this.shown = true; });
    },

    close() {
        if (!this.visible) return;
        this.shown = false; // fade-out
        this._closeTimer = setTimeout(() => {
            this.visible = false;
            // Clear the src so the iframe/img is unloaded (frees memory, stops the embedded PDF)
            this._revokeObjectUrl();
            this.url = '';
        }, 150); // keep in sync with the CSS transition duration
    },

    /**
     * Revoke the current URL if it is an object URL (preview of a not-yet-uploaded file).
     * Server preview URLs (http/relative) are left untouched.
     */
    _revokeObjectUrl() {
        if (this.url && this.url.startsWith('blob:')) {
            URL.revokeObjectURL(this.url);
        }
    }
});
